import { apiClient } from "./client"

import type {
  AdaptiveLevel,
  AdaptiveLevelActionResponse,
  AdaptiveLevelDetailResponse,
  AdaptiveLevelRosterResponse,
  AdaptiveMode,
} from "@/types"

export interface AdaptivePageParams {
  page?: number
  page_size?: number
}

/** Mirrors apps/backend/api/schemas.py::AdaptiveModeOut. */
export interface AdaptiveModeResponse {
  adaptive_mode: AdaptiveMode
  automatic_available: boolean
}

const boundedInteger = (value: number | undefined, fallback: number, maximum?: number) => {
  if (!Number.isFinite(value)) return fallback
  const integer = Math.max(1, Math.trunc(value as number))
  return maximum === undefined ? integer : Math.min(maximum, integer)
}

const pageParams = (params: AdaptivePageParams = {}) => {
  const page = boundedInteger(params.page, 1)
  const pageSize = boundedInteger(params.page_size, 50, 100)
  return {
    ...(page > 1 ? { page } : {}),
    ...(pageSize !== 50 ? { page_size: pageSize } : {}),
  }
}

export const adaptiveLevelsApi = {
  listClassroom: (classroomId: string, params: AdaptivePageParams = {}) =>
    apiClient
      .get<AdaptiveLevelRosterResponse>(`/classrooms/${classroomId}/adaptive-levels`, {
        params: pageParams(params),
      })
      .then((response) => response.data),

  getStudent: (studentId: string, params: AdaptivePageParams = {}) =>
    apiClient
      .get<AdaptiveLevelDetailResponse>(`/students/${studentId}/adaptive-level`, {
        params: pageParams(params),
      })
      .then((response) => response.data),

  setMode: (classroomId: string, mode: AdaptiveMode) =>
    apiClient
      .patch<AdaptiveModeResponse>(`/classrooms/${classroomId}/adaptive-mode`, { mode })
      .then((response) => response.data),

  accept: (studentId: string, evaluationId: string) =>
    apiClient
      .post<AdaptiveLevelActionResponse>(`/students/${studentId}/adaptive-level/accept`, {
        evaluation_id: evaluationId,
      })
      .then((response) => response.data),

  reject: (studentId: string, evaluationId: string, reason: string) =>
    apiClient
      .post<AdaptiveLevelActionResponse>(`/students/${studentId}/adaptive-level/reject`, {
        evaluation_id: evaluationId,
        reason,
      })
      .then((response) => response.data),

  override: (
    studentId: string,
    payload: {
      idempotency_key: string
      target_level: AdaptiveLevel
      reason: string
      pin: boolean
    },
  ) =>
    apiClient
      .post<AdaptiveLevelActionResponse>(`/students/${studentId}/adaptive-level/override`, payload)
      .then((response) => response.data),

  pin: (studentId: string, reason: string) =>
    apiClient
      .post<AdaptiveLevelActionResponse>(`/students/${studentId}/adaptive-level/pin`, { reason })
      .then((response) => response.data),

  /** Reverts the professor's most recent accept / override when nothing
   *  newer happened; 409 `adaptive_undo_unavailable` otherwise. */
  undo: (studentId: string) =>
    apiClient
      .post<AdaptiveLevelDetailResponse>(`/students/${studentId}/adaptive-level/undo`)
      .then((response) => response.data),

  unpin: (studentId: string, reason: string) =>
    apiClient
      .post<AdaptiveLevelActionResponse>(`/students/${studentId}/adaptive-level/unpin`, { reason })
      .then((response) => response.data),
}
